import Phaser from 'phaser';
import { Enemy } from '@/entities/Enemy';
import { PATHS, SPAWN_POSITIONS, MAP_GRID } from '@/map/MapData';
import {
  WAVE_CONFIG,
  EnemyType,
  EnemyGrade,
  ActorType,
  TILE_SIZE,
  ENEMY_BASE_HP_BASE,
  ENEMY_BASE_HP_GROWTH,
  TileType,
} from '@/config/GameConfig';
import { Actor } from '@/entities/Actor';
import { EventBus, GameEvents } from '@/events/EventBus';
import { GOD, getEnemyTypeStatOverride } from '@/config/GodMode';
import { Robot } from '@/entities/Robot';
import { chooseFuzzyPath } from '@/systems/ThreatAssessor';
import { composeMap, mapWaves, WaveEntry } from '@/systems/WaveComposer';
import { SpatialHash } from '@/systems/SpatialHash';
import { BilliardKnockback } from '@/systems/BilliardKnockback';

// ── Spawn queue item: either an enemy to spawn or a pause between groups ──
type SpawnItem =
  | { kind: 'enemy'; type: EnemyType; grade: EnemyGrade }
  | { kind: 'pause'; duration: number };

export class WaveManager {
  private scene: Phaser.Scene;
  public enemies: Enemy[] = [];
  /** Spatial hash for fast enemy-enemy proximity queries */
  public enemyHash = new SpatialHash<Enemy>(40);
  public currentWave: number = 0;
  public waveActive: boolean = false;
  public enemiesSpawned: number = 0;
  /** Enemies still alive + not yet spawned (computed, not decremented) */
  get enemiesRemaining(): number {
    if (!this.waveActive) return 0;
    const totalInQueue = this.spawnQueue.filter(i => i.kind === 'enemy').length;
    const notYetSpawned = totalInQueue - this.enemiesSpawned;
    const aliveCount = this.enemies.filter(e => e.alive).length;
    return aliveCount + notYetSpawned;
  }
  public totalWaves: number = 5;
  public allWavesComplete: boolean = false;
  /** Destructible enemy bases at spawn positions */
  public enemyBases: Actor[] = [];
  /** True when all enemy bases have been destroyed */
  public allBasesDestroyed = false;
  /** Number of enemy bases destroyed */
  get enemyBasesDestroyed(): number { return this.enemyBases.filter(b => !b.alive).length; }
  /** Total number of enemy bases on the map */
  get enemyBasesTotal(): number { return this.enemyBases.length; }

  private tier: number = 0;
  private mapIndex: number = 1;        // 1-99, used by WaveComposer for power budget
  private precomposedWaves: WaveEntry[][] = []; // all waves pre-composed at start
  private spawnDelayMs: number = 0;    // 0 = use default WAVE_CONFIG.spawnDelay

  /** Preview the next wave's enemy composition (for CPU bot / UI preview) */
  getNextWaveEntries(): WaveEntry[] {
    const nextIdx = this.currentWave; // currentWave is 0-based after start, next = current
    return nextIdx < this.precomposedWaves.length ? this.precomposedWaves[nextIdx] : [];
  }
  private spawnQueue: SpawnItem[] = [];
  private spawnIndex: number = 0;
  private spawnTimer: Phaser.Time.TimerEvent | null = null;
  private pathIndex: number = 0;

  /** Optional callback to get alive robots for fuzzy path selection. */
  private getRobots: (() => Robot[]) | null = null;

  /** Billiard knockback physics system */
  public billiard: BilliardKnockback;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.billiard = new BilliardKnockback();
    EventBus.on(GameEvents.ENEMY_DESTROYED, this.onEnemyRemoved, this);
    EventBus.on(GameEvents.ENEMY_REACHED_BASE, this.onEnemyRemoved, this);
    EventBus.on(GameEvents.ENEMY_BASE_DESTROYED, this.onActorDestroyed, this);
    EventBus.on(GameEvents.HIVE_DEATH_SPAWN, this.onHiveDeathSpawn, this);
    EventBus.on(GameEvents.BASE_NODE_DESTROYED, this.onBaseNodeDestroyed, this);
  }

  /** Set tier (0-32) and map index (1-99) — determines power budget and wave count.
   *  If overrideWaves is provided (e.g. from map definition), it overrides the computed wave count. */
  setTier(tier: number, mapIdx?: number, overrideWaves?: number, flat?: boolean, spawnDelayMs?: number, allowedEnemies?: EnemyType[], intensityMult?: number, allowedGrades?: EnemyGrade[]): void {
    this.tier = tier;
    // Convert tier to approximate map index if not provided
    this.mapIndex = mapIdx ?? Math.max(1, Math.min(99, Math.round(1 + tier * 98 / 32)));
    this.totalWaves = overrideWaves ?? mapWaves(this.mapIndex);
    this.spawnDelayMs = spawnDelayMs ?? 0;
    // Pre-compose all waves using power budget system
    this.precomposedWaves = composeMap(this.mapIndex, this.totalWaves, flat, allowedEnemies, intensityMult, tier, allowedGrades);
    // Adjust totalWaves to match what was actually composed
    this.totalWaves = this.precomposedWaves.length;
  }

  /** Create destructible enemy bases at each spawn position. */
  createBases(tier: number): void {
    for (let i = 0; i < SPAWN_POSITIONS.length; i++) {
      const pos = SPAWN_POSITIONS[i];
      const col = Math.floor(pos.x / TILE_SIZE);
      const row = Math.floor(pos.y / TILE_SIZE);
      const hp = Math.round(ENEMY_BASE_HP_BASE * Math.pow(ENEMY_BASE_HP_GROWTH, tier));
      const base = new Actor(this.scene, col, row, ActorType.ENEMY_BASE, {
        hp,
        textureKey: 'actor_enemy_base',
        id: `enemyBase_${i}`,
        pathIndex: i,
      });
      this.enemyBases.push(base);
    }
  }

  /** Set the robot provider for threat-aware path selection. */
  setRobotProvider(fn: () => Robot[]): void {
    this.getRobots = fn;
  }

  // ─────────────────────── Wave lifecycle ───────────────────────

  startWave(): void {
    if (this.waveActive || this.allWavesComplete) return;

    this.currentWave++;
    this.waveActive = true;
    this.enemiesSpawned = 0;
    this.spawnIndex = 0;

    this.spawnQueue = this.buildSpawnQueue(this.currentWave);

    EventBus.emit(GameEvents.WAVE_STARTED, this.currentWave);

    // Kick off the self-scheduling spawn loop
    const delay = this.spawnDelayMs || Math.round(WAVE_CONFIG.spawnDelay * GOD.spawnDelayMult);
    this.scheduleNext(delay);
  }

  // ─────────────────────── Spawn loop ───────────────────────

  private scheduleNext(delayMs: number): void {
    this.spawnTimer = this.scene.time.delayedCall(delayMs, () => this.processNext());
  }

  private processNext(): void {
    if (this.spawnIndex >= this.spawnQueue.length) return;

    const item = this.spawnQueue[this.spawnIndex];
    this.spawnIndex++;

    if (item.kind === 'pause') {
      // Wait, then continue to next item
      this.scheduleNext(item.duration);
      return;
    }

    // Spawn the enemy
    this.spawnEnemy(item);

    // Schedule next item if any remain
    if (this.spawnIndex < this.spawnQueue.length) {
      const delay = this.spawnDelayMs || Math.round(WAVE_CONFIG.spawnDelay * GOD.spawnDelayMult);
      this.scheduleNext(delay);
    }
  }

  private spawnEnemy(entry: { type: EnemyType; grade: EnemyGrade }): void {
    // If all enemy bases are destroyed, count as spawned (skipped) so wave can end
    if (this.allBasesDestroyed) {
      this.enemiesSpawned++;
      return;
    }

    // Fuzzy path selection: evaluate threat per corridor
    const robots = this.getRobots?.() ?? [];
    let pathIdx = robots.length > 0
      ? chooseFuzzyPath(PATHS, robots)
      : this.pathIndex % PATHS.length;
    this.pathIndex++;

    // If the chosen path's base is destroyed, pick another alive base
    if (this.enemyBases.length > 0 && pathIdx < this.enemyBases.length && !this.enemyBases[pathIdx].alive) {
      const aliveBaseIdx = this.enemyBases.findIndex(b => b.alive);
      if (aliveBaseIdx < 0) {
        // All bases destroyed — count as spawned so wave can end
        this.enemiesSpawned++;
        return;
      }
      pathIdx = aliveBaseIdx % PATHS.length;
    }

    const path = PATHS[pathIdx];

    // Stat scaling: tier contributes bulk, wave adds per-level progression
    const waveScaling = this.tier + (this.currentWave - 1);

    const enemy = new Enemy(this.scene, path, entry.type, waveScaling, entry.grade);
    this.enemies.push(enemy);
    this.enemiesSpawned++;
  }

  // ─────────────────────── Wave composition (power-budget based) ───────────────────────

  /**
   * Build the spawn queue for a wave from pre-composed WaveEntry list.
   * Splits enemies into groups with pauses between them.
   */
  private buildSpawnQueue(wave: number): SpawnItem[] {
    // Get pre-composed enemies for this wave (0-indexed)
    const waveIdx = wave - 1;
    const entries: WaveEntry[] = waveIdx < this.precomposedWaves.length
      ? this.precomposedWaves[waveIdx]
      : [{ type: EnemyType.GRUNT, grade: EnemyGrade.NORMAL }]; // fallback

    const totalCount = entries.length;

    // Number of groups: escalates through the waves (1 → up to 4)
    const numGroups = Math.min(4, 1 + Math.floor((wave - 1) / 2));
    const groupSizes = this.splitIntoGroups(totalCount, numGroups);

    // Pause between groups: shorter at higher tiers
    const pauseMs = Math.max(500, 3000 - this.tier * 80);

    const queue: SpawnItem[] = [];
    let entryIdx = 0;
    for (let g = 0; g < groupSizes.length; g++) {
      if (g > 0) {
        queue.push({ kind: 'pause', duration: pauseMs });
      }
      const groupEnemies: SpawnItem[] = [];
      for (let i = 0; i < groupSizes[g] && entryIdx < entries.length; i++) {
        const e = entries[entryIdx++];
        groupEnemies.push({ kind: 'enemy', type: e.type, grade: e.grade });
      }
      // Shuffle within group for variety
      for (let i = groupEnemies.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [groupEnemies[i], groupEnemies[j]] = [groupEnemies[j], groupEnemies[i]];
      }
      queue.push(...groupEnemies);
    }

    return queue;
  }

  /** Distribute `total` enemies into `numGroups`, later groups slightly larger. */
  private splitIntoGroups(total: number, numGroups: number): number[] {
    const base = Math.floor(total / numGroups);
    const sizes = Array(numGroups).fill(base);
    let remainder = total - base * numGroups;
    for (let i = numGroups - 1; i >= 0 && remainder > 0; i--) {
      sizes[i]++;
      remainder--;
    }
    return sizes;
  }

  // ─────────────────────── Enemy tracking ───────────────────────

  private onEnemyRemoved = (enemy: Enemy): void => {
    if (!this.enemies.includes(enemy)) return;

    if (this.enemiesRemaining <= 0 && this.waveActive) {
      this.waveActive = false;
      EventBus.emit(GameEvents.WAVE_COMPLETED, this.currentWave);

      if (this.currentWave >= this.totalWaves) {
        this.allWavesComplete = true;
      }
    }

    // Check if all enemy bases gone + last enemy just died → victory
    this.checkAllBasesVictory();
  };

  private onActorDestroyed = (): void => {
    // Check if all enemy bases are now destroyed
    const anyAlive = this.enemyBases.some(b => b.alive);
    if (!anyAlive && this.enemyBases.length > 0) {
      this.allBasesDestroyed = true;
      // If no enemies left on screen either → immediate victory
      this.checkAllBasesVictory();
    }
  };

  /** A player base node was destroyed — repath all enemies to surviving bases */
  private onBaseNodeDestroyed = (): void => {
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;
      // Enemies currently sieging the dead base need to repath
      if (enemy.attackingBase) {
        enemy.attackingBase = false;
        enemy.reachedBase = false;
      }
      // Force A* recalculation for all ground enemies
      enemy.markPathDirty();
    }
  };

  /** Victory condition: all enemy bases destroyed + no enemies alive on screen */
  private checkAllBasesVictory(): void {
    if (!this.allBasesDestroyed) return;
    const aliveEnemies = this.enemies.filter(e => e.alive).length;
    if (aliveEnemies === 0) {
      // Stop spawning — mark all waves complete
      this.waveActive = false;
      this.allWavesComplete = true;
      if (this.spawnTimer) { this.spawnTimer.destroy(); this.spawnTimer = null; }
      EventBus.emit(GameEvents.WAVE_COMPLETED, this.currentWave);
    }
  }

  // ─────────────────────── Update & cleanup ───────────────────────

  update(_time: number, delta: number): void {
    // Pre-compute firing targets so arc formation knows who's shooting where
    for (const e of this.enemies) {
      if (e.alive) e.precomputeFiringTarget();
    }
    // Compute separation + arc steering BEFORE enemy movement
    this.computeSeparation();

    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      if (enemy.alive) {
        enemy.update(delta);
      } else {
        this.enemies.splice(i, 1);
      }
    }

    // Billiard knockback physics (velocity-based bouncing)
    const alive = this.enemies.filter(e => e.alive);
    this.billiard.update(delta / 1000, alive);

    // Healer pulse: heal nearby allies
    this.processHealers(delta / 1000, alive);


    this.resolveEnemyCollisions();

    // Safety net: if wave is active but nothing left, force completion
    if (this.waveActive && this.enemiesRemaining <= 0) {
      this.waveActive = false;
      EventBus.emit(GameEvents.WAVE_COMPLETED, this.currentWave);
      if (this.currentWave >= this.totalWaves) {
        this.allWavesComplete = true;
      }
    }
  }

  /**
   * Compute proactive separation steering vectors for each enemy,
   * plus arc-formation steering for enemies sharing a firing target.
   */
  private computeSeparation(): void {
    const alive = this.enemies.filter(e => e.alive);
    this.enemyHash.rebuild(alive, e => e.body.x, e => e.body.y);
    const SEP_RADIUS = 60;

    for (const e of alive) {
      e.separationX = 0;
      e.separationY = 0;
      e.arcSteerX = 0;
      e.arcSteerY = 0;
      const neighbors = this.enemyHash.queryRadius(e.body.x, e.body.y, SEP_RADIUS);
      let count = 0;
      for (const n of neighbors) {
        if (n === e) continue;
        if (n.isFlyer !== e.isFlyer) continue;
        // Runners & Swarms pass through other enemies — no separation
        if (e.enemyType === EnemyType.RUNNER || n.enemyType === EnemyType.RUNNER) continue;
        if (e.enemyType === EnemyType.SWARM || n.enemyType === EnemyType.SWARM) continue;
        const dx = e.body.x - n.body.x;
        const dy = e.body.y - n.body.y;
        const distSq = dx * dx + dy * dy;
        if (distSq < SEP_RADIUS * SEP_RADIUS && distSq > 0.01) {
          const dist = Math.sqrt(distSq);
          const weight = 1 - dist / SEP_RADIUS;
          e.separationX += (dx / dist) * weight;
          e.separationY += (dy / dist) * weight;
          count++;
        }
      }
      if (count > 0) {
        const len = Math.sqrt(e.separationX * e.separationX + e.separationY * e.separationY);
        if (len > 0.001) {
          e.separationX /= len;
          e.separationY /= len;
        }
      }
    }

    // ── Arc formation for enemies sharing a firing target ──
    // Group enemies by their firing target (quantized to tile)
    const groups = new Map<string, typeof alive>();
    for (const e of alive) {
      if (!e.firingTarget) continue;
      // Quantize target to tile grid so nearby targets merge
      const key = `${Math.round(e.firingTarget.x / 20)},${Math.round(e.firingTarget.y / 20)}`;
      let group = groups.get(key);
      if (!group) { group = []; groups.set(key, group); }
      group.push(e);
    }

    for (const group of groups.values()) {
      if (group.length < 2) continue;
      const tx = group[0].firingTarget!.x;
      const ty = group[0].firingTarget!.y;

      // Compute group centroid for a stable anchor angle (centroid moves slowly → damped)
      let cx = 0, cy = 0;
      for (const e of group) { cx += e.body.x; cy += e.body.y; }
      cx /= group.length;
      cy /= group.length;
      const anchorAngle = Math.atan2(cy - ty, cx - tx);

      // Sort by angle around target for consistent slot assignment
      const sorted = group.map(e => {
        const angle = Math.atan2(e.body.y - ty, e.body.x - tx);
        return { e, angle };
      }).sort((a, b) => a.angle - b.angle);

      // Desired angular spacing between enemies (min ~25° = 0.44 rad)
      const minSpacing = 0.44;
      const totalArc = Math.min(Math.PI * 1.5, (sorted.length - 1) * minSpacing);
      // Center the arc on the stable centroid angle instead of the shifting median
      const startAngle = anchorAngle - totalArc / 2;

      for (let i = 0; i < sorted.length; i++) {
        const e = sorted[i].e;
        const desiredAngle = startAngle + (totalArc * i) / Math.max(1, sorted.length - 1);
        const currentAngle = sorted[i].angle;

        // Tangential steering: push perpendicular to the radius (toward desired angle)
        let angleDiff = desiredAngle - currentAngle;
        while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
        while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

        // Perpendicular to radius = tangent direction
        const rdx = e.body.x - tx;
        const rdy = e.body.y - ty;
        const rDist = Math.sqrt(rdx * rdx + rdy * rdy);
        if (rDist < 1) continue;
        // Tangent (counterclockwise = positive angle direction)
        const tangX = -rdy / rDist;
        const tangY = rdx / rDist;
        // Deadzone: ignore tiny angle differences to prevent oscillation
        const absAngle = Math.abs(angleDiff);
        if (absAngle < 0.05) continue; // ~3° deadzone
        const steerStrength = Math.min(1, (absAngle - 0.05) * 2) * Math.sign(angleDiff);
        e.arcSteerX = tangX * steerStrength;
        e.arcSteerY = tangY * steerStrength;
      }
    }
  }

  /**
   * Healer pulse: each HEALER enemy periodically heals nearby allies.
   * Uses the spatial hash (already built in computeSeparation) for efficient neighbor lookup.
   * Emits HEALER_PULSE event for VFX (JuiceEffects).
   */
  private processHealers(dt: number, alive: Enemy[]): void {
    for (const healer of alive) {
      if (healer.enemyType !== EnemyType.HEALER) continue;
      healer.healTickTimer -= dt;
      if (healer.healTickTimer > 0) continue;
      healer.healTickTimer = GOD.healerCooldown;

      // Find nearby allies within heal range
      const range = GOD.healerRange;
      const neighbors = this.enemyHash.queryRadius(healer.body.x, healer.body.y, range);
      let healed = 0;
      for (const ally of neighbors) {
        if (healed >= GOD.healerMaxTargets) break;
        if (!ally.alive) continue;
        if (ally === healer && !GOD.healerSelfHeal) continue;
        if (ally.hp >= ally.maxHp) continue; // already full HP
        const dx = ally.body.x - healer.body.x;
        const dy = ally.body.y - healer.body.y;
        if (dx * dx + dy * dy > range * range) continue;

        const ov = getEnemyTypeStatOverride(EnemyType.HEALER);
        const healAmt = Math.min(GOD.healerAmount * ov.healMult, ally.maxHp - ally.hp);
        ally.hp += healAmt;
        // Update HP bar
        ally.hpBar?.setScale(ally.hp / ally.maxHp, 1);
        healed++;

        // Green heal particle on the ally
        this.spawnHealParticle(ally);
      }

      if (healed > 0) {
        // Visual flash on the healer's aura
        if (healer.healAura) {
          healer.healAura.setAlpha(0.35);
          healer.scene.tweens.add({
            targets: healer.healAura,
            alpha: 0.1,
            duration: 400,
            ease: 'Cubic.easeOut',
          });
        }
        EventBus.emit(GameEvents.HEALER_PULSE, {
          x: healer.body.x, y: healer.body.y, count: healed,
        });
      }
    }
  }

  /** Spawn a small green "+" particle on a healed ally */
  private spawnHealParticle(enemy: Enemy): void {
    const txt = this.scene.add.text(
      enemy.body.x + (Math.random() - 0.5) * 10,
      enemy.body.y - 12,
      '+',
      { fontSize: '14px', color: '#44ff88', fontFamily: 'monospace', stroke: '#004422', strokeThickness: 2 },
    ).setOrigin(0.5).setDepth(20);
    this.scene.tweens.add({
      targets: txt,
      y: txt.y - 20,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeOut',
      onComplete: () => txt.destroy(),
    });
  }

  /**
   * Push overlapping enemies apart (ground-ground and air-air separately).
   * Uses spatial hash to avoid O(n²) brute-force: only checks nearby pairs.
   */
  private resolveEnemyCollisions(): void {
    const alive = this.enemies.filter(e => e.alive);
    // Rebuild spatial hash for this frame
    this.enemyHash.rebuild(alive, e => e.body.x, e => e.body.y);

    // Track resolved pairs to avoid double-processing (a,b) and (b,a)
    const resolved = new Set<string>();
    const MAX_QUERY_R = 40; // max collision diameter (2 × largest radius 16 + margin)

    for (let i = 0; i < alive.length; i++) {
      const a = alive[i];
      const neighbors = this.enemyHash.queryRadius(a.body.x, a.body.y, MAX_QUERY_R);
      for (let j = 0; j < neighbors.length; j++) {
        const b = neighbors[j];
        if (a === b) continue;
        if (a.isFlyer !== b.isFlyer) continue;
        // Runners & Swarms pass through other enemies — no collision push
        if (a.enemyType === EnemyType.RUNNER || b.enemyType === EnemyType.RUNNER) continue;
        if (a.enemyType === EnemyType.SWARM || b.enemyType === EnemyType.SWARM) continue;
        // Skip enemies in billiard knockback — handled by BilliardKnockback system
        if (a.isKnockedBack || b.isKnockedBack) continue;

        // Deduplicate: use ordered id pair
        const pairKey = a.id < b.id ? `${a.id}_${b.id}` : `${b.id}_${a.id}`;
        if (resolved.has(pairKey)) continue;
        resolved.add(pairKey);

        const dx = b.body.x - a.body.x;
        const dy = b.body.y - a.body.y;
        const distSq = dx * dx + dy * dy;
        const minDist = a.collisionRadius + b.collisionRadius;
        if (distSq < minDist * minDist && distSq > 0.0001) {
          const dist = Math.sqrt(distSq);
          const overlap = minDist - dist;
          const nx = dx / dist;
          const ny = dy / dist;
          const totalMass = a.mass + b.mass;
          let pushA = overlap * (b.mass / totalMass);
          let pushB = overlap * (a.mass / totalMass);
          // Reduce collision push for enemies in arc formation (they have arc steering for spacing)
          if (a.firingTarget) pushA *= 0.3;
          if (b.firingTarget) pushB *= 0.3;
          // Only accept push if target tile is walkable (prevent wall penetration)
          const ax = a.body.x - nx * pushA;
          const ay = a.body.y - ny * pushA;
          if (a.isFlyer || this.isTileWalkable(ax, ay)) {
            a.body.setPosition(ax, ay);
            if (!a.firingTarget) a.markPathDirty();
          }
          const bx = b.body.x + nx * pushB;
          const by = b.body.y + ny * pushB;
          if (b.isFlyer || this.isTileWalkable(bx, by)) {
            b.body.setPosition(bx, by);
            if (!b.firingTarget) b.markPathDirty();
          }
        }
      }
    }
  }

  /** Check if a world position lands on a walkable tile */
  private isTileWalkable(x: number, y: number): boolean {
    const col = Math.floor(x / TILE_SIZE);
    const row = Math.floor(y / TILE_SIZE);
    const tile = MAP_GRID[row]?.[col];
    return tile === TileType.PATH || tile === TileType.PLACEMENT ||
      tile === TileType.BROKEN_PLACEMENT || tile === TileType.BASE ||
      tile === TileType.SPAWN || tile === TileType.MUD || tile === TileType.GRILLE;
  }

  // ─────────────────────── Hive death-spawn ───────────────────────

  private onHiveDeathSpawn = (data: { x: number; y: number; count: number; grade: EnemyGrade; waveScaling: number }): void => {
    // Spawn Wasps at the Hive death position
    // Find nearest valid waypoint path for spawned Wasps (use closest path)
    const { x, y, count, grade, waveScaling } = data;

    for (let i = 0; i < count; i++) {
      // Slight offset so Wasps don't stack perfectly
      const spawnX = x + (Math.random() - 0.5) * 20;
      const spawnY = y + (Math.random() - 0.5) * 20;

      // Build a path from death position to the base (last waypoint of any path)
      const closestPath = PATHS[0]; // Wasps fly, so any base point works
      const basePt = closestPath[closestPath.length - 1];
      const syntheticPath = [{ x: spawnX, y: spawnY }, basePt];

      // Wasps spawned from Hive inherit grade (capped at ELITE to avoid Gold wasps)
      const spawnGrade = grade === EnemyGrade.NORMAL ? EnemyGrade.NORMAL : EnemyGrade.ELITE;

      const wasp = new Enemy(this.scene, syntheticPath, EnemyType.WASP, waveScaling, spawnGrade);
      this.enemies.push(wasp);

      // Spawn VFX — green burst
      const burst = this.scene.add.circle(spawnX, spawnY, 8, 0x66ff22, 0.7).setDepth(25);
      this.scene.tweens.add({
        targets: burst, alpha: 0, scaleX: 3, scaleY: 3,
        duration: 300, onComplete: () => burst.destroy(),
      });
    }
  };

  shutdown(): void {
    EventBus.off(GameEvents.ENEMY_DESTROYED, this.onEnemyRemoved, this);
    EventBus.off(GameEvents.ENEMY_REACHED_BASE, this.onEnemyRemoved, this);
    EventBus.off(GameEvents.ENEMY_BASE_DESTROYED, this.onActorDestroyed, this);
    EventBus.off(GameEvents.HIVE_DEATH_SPAWN, this.onHiveDeathSpawn, this);
    EventBus.off(GameEvents.BASE_NODE_DESTROYED, this.onBaseNodeDestroyed, this);
    if (this.spawnTimer) {
      this.spawnTimer.destroy();
    }
  }
}
